'use client';

/**
 * Content Tracker Component
 *
 * A React component for tracking ViewContent events on content pages.
 * This replaces the previous ContentViewTracker implementation with a more
 * standardized approach that integrates with our unified analytics system.
 */
import { useEffect } from 'react';
import { useTrackEvent } from '../hooks/useTrackEvent';
import { getConsentData } from '@/components/cookie-modal';
import * as gtag from '../gtag';
export interface ContentTrackerProps {
  /**
   * Type of content (department, treatment, etc.)
   */
  contentType: string;

  /**
   * ID or slug of the content
   */
  contentId: string;

  /**
   * Human-readable name of the content
   */
  contentName: string;

  /**
   * Whether the content contains health-related information
   * Used for GDPR health data consent checks
   */
  isHealthRelated?: boolean;

  /**
   * Optional user info for enhanced matching
   */
  userData?: {
    email?: string;
    phone?: string;
    firstName?: string;
    lastName?: string;
    [key: string]: any;
  };
}

/**
 * Component for tracking ViewContent events on content pages.
 * This is a client-side only component that fires once when the page is viewed.
 *
 * Usage:
 * ```jsx
 * <ContentTracker
 *   contentType="department"
 *   contentId="lipogems"
 *   contentName="Lipogems Department"
 *   isHealthRelated={true}
 * />
 * ```
 */
export function ContentTracker({
  contentType,
  contentId,
  contentName,
  isHealthRelated = false,
  userData
}: ContentTrackerProps) {
  const {
    track
  } = useTrackEvent();
  useEffect(() => {
    // Skip in SSR context
    if (typeof window === 'undefined') {
      return;
    }

    // Check for cookie consent
    const consentData = getConsentData();
    if (!consentData?.measurement) {
      console.log('[ContentTracker] Skipping tracking - no measurement consent');
      return;
    }

    // Generate a unique event ID for deduplication
    const eventId = `content_view_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

    // Track the view content event with Meta Pixel
    track({
      name: 'view_content',
      properties: {
        content_type: contentType,
        content_id: contentId,
        content_name: contentName,
        source_url: window.location.href
      },
      options: {
        isHealthRelated,
        userInfo: userData,
        eventId
      }
    });

    // Also track with Google Analytics if available
    if (typeof window.gtag === 'function') {
      gtag.event({
        action: 'view_content',
        category: contentType,
        label: contentName,
        value: 1
      });
      console.log(`[ContentTracker] Tracked GA event for ${contentType}: ${contentName}`);
    }
  }, [contentType, contentId, contentName, isHealthRelated, userData, track]);

  // Component doesn't render anything
  return null;
}